/*
 * <load-more-button> — AJAX pagination for the collection / search product grid.
 *
 * Progressive enhancement:
 *   The section always renders Dawn's numbered `{% render 'pagination' %}`. With JavaScript
 *   disabled that pagination is the only thing visible and works normally. When this element
 *   upgrades it hides the pagination and reveals itself instead.
 *
 * Behaviour:
 *   - fetches `data-next-url` (the value of `paginate.next.url`)
 *   - parses the response with DOMParser and pulls the grid items out of the SAME section id
 *   - appends them to the live grid (never replaces it, so scroll position is untouched)
 *   - re-reads `data-next-url` from the fetched page, or removes itself on the last page
 *   - updates the address bar with history.replaceState so a refresh lands roughly in place
 *   - announces the addition through an aria-live="polite" region
 *   - falls back to a plain navigation to the next URL if anything goes wrong
 */

if (!customElements.get('load-more-button')) {
  customElements.define(
    'load-more-button',
    class LoadMoreButton extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.scope = this.closest('[data-collection-grid-scope]') || document;
        this.button = this.querySelector('[data-load-more-button]');
        this.label = this.querySelector('[data-load-more-label]');
        // The live region lives outside this element so announcements survive its removal.
        this.status = this.scope.querySelector('[data-load-more-status]');
        this.loading = false;

        if (!this.button) return;

        this.loadingText = this.dataset.loadingText || '';
        this.idleText = this.label ? this.label.textContent : '';

        this.onClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.onClick);

        // Reveal the button and hide the no-JS pagination.
        this.setAttribute('data-enhanced', 'true');
        this.togglePagination(true);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onClick);
      }

      get sectionId() {
        return this.dataset.sectionId || '';
      }

      get nextUrl() {
        return this.dataset.nextUrl || '';
      }

      /* The pagination lives next to this element inside the same section. */
      togglePagination(enhanced) {
        const pagination = (this.scope || document).querySelector('[data-collection-pagination]');
        if (pagination) pagination.classList.toggle('collection-pagination--enhanced', enhanced);
      }

      findGrid(root) {
        if (!root) return null;
        return (
          (this.sectionId && root.querySelector(`#product-grid[data-id="${this.sectionId}"]`)) ||
          root.querySelector('#product-grid') ||
          null
        );
      }

      setLoading(isLoading) {
        this.loading = isLoading;
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.button.disabled = isLoading;
        if (this.label) this.label.textContent = isLoading ? this.loadingText || this.idleText : this.idleText;
        if (isLoading) this.announce(this.loadingText);
      }

      announce(message) {
        if (this.status && message) this.status.textContent = message;
      }

      announceAdded(count) {
        const template = this.dataset.countTemplate;
        if (!template) return;
        this.announce(template.replace('__count__', count));
      }

      onClick(event) {
        event.preventDefault();
        this.loadMore();
      }

      loadMore() {
        const url = this.nextUrl;
        if (this.loading || !url) return;

        const grid = this.findGrid(document);
        if (!grid) return;

        const hadFocus = document.activeElement === this.button;
        this.setLoading(true);

        fetch(url)
          .then((response) => {
            if (!response.ok) throw new Error(`Load more failed with status ${response.status}`);
            return response.text();
          })
          .then((text) => {
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const sourceGrid = this.findGrid(parsed);
            if (!sourceGrid) throw new Error('Load more: product grid not found in response');

            const items = Array.from(sourceGrid.children);
            if (items.length === 0) throw new Error('Load more: response contained no products');

            const fragment = document.createDocumentFragment();
            items.forEach((item) => fragment.appendChild(document.importNode(item, true)));

            const firstNewItem = fragment.firstElementChild;
            // Appending below the fold cannot move the scroll position.
            grid.appendChild(fragment);

            if (firstNewItem) {
              firstNewItem.setAttribute('tabindex', '-1');
              // preventScroll keeps the viewport exactly where the shopper left it.
              if (hadFocus) firstNewItem.focus({ preventScroll: true });
            }

            if (typeof initializeScrollAnimationTrigger === 'function') {
              initializeScrollAnimationTrigger(grid);
            }

            // Keep a refresh roughly in place without adding a history entry. The state shape
            // matches facets.js (`{ searchParams }`) so its popstate handler keeps working.
            try {
              const queryIndex = url.indexOf('?');
              const searchParams = queryIndex === -1 ? '' : url.slice(queryIndex + 1);
              window.history.replaceState({ searchParams }, '', url);
            } catch (historyError) {
              // Unsupported or blocked — harmless, the products are already on the page.
              console.warn(historyError);
            }

            const nextButton = parsed.querySelector('load-more-button');
            const newNextUrl = nextButton ? nextButton.getAttribute('data-next-url') : '';

            this.announceAdded(items.length);

            if (newNextUrl) {
              this.dataset.nextUrl = newNextUrl;
              this.setLoading(false);
            } else {
              // Last page: everything is on the page now, so the button goes away and the
              // numbered pagination stays hidden. Focus remains on the newly appended card.
              this.remove();
            }
          })
          .catch((error) => {
            console.error(error);
            // Graceful degradation: behave like the plain pagination link.
            window.location.href = url;
          });
      }
    }
  );
}
